import { api, del, get, post, put, workspaceHeaders } from '../client'
import type {
  AudioPreferences,
  CreateWorkspaceRequest,
  InviteToWorkspaceRequest,
  PilotCreditApplied,
  TrialProfile,
  UpdateMemberRoleRequest,
  UpdateWorkspaceRequest,
  Workspace,
  WorkspaceFeatureCheckResponse,
  WorkspaceInvitation,
  WorkspaceMember,
  WorkspaceSummary,
  WorkspaceUsageSummary,
  WorkspaceWithMembers,
} from '../types/generated'

export async function fetchWorkspaces(): Promise<WorkspaceSummary[]> {
  const response = await get<WorkspaceSummary[]>('api/hm/workspace/list')
  return response ?? []
}

export async function fetchWorkspace(): Promise<WorkspaceWithMembers | null> {
  const response = await get<WorkspaceWithMembers | null>('api/hm/workspace')
  return response
}

export async function createWorkspace(data: CreateWorkspaceRequest): Promise<WorkspaceWithMembers> {
  const response = await post<WorkspaceWithMembers>('api/hm/workspace', data)
  if (!response) {
    throw new Error('Failed to create workspace')
  }
  return response
}

export async function updateWorkspace(data: UpdateWorkspaceRequest): Promise<Workspace> {
  const response = await put<Workspace>('api/hm/workspace', data)
  if (!response) {
    throw new Error('Failed to update workspace')
  }
  return response
}

export async function updateWorkspaceAudioPreferences(
  audioPreferences: AudioPreferences
): Promise<Workspace> {
  return updateWorkspace({ audio_preferences: audioPreferences })
}

export async function deleteWorkspace(): Promise<void> {
  await del('api/hm/workspace')
}

export async function uploadWorkspaceLogo(
  file: File,
  variant: 'light' | 'dark' = 'light'
): Promise<{ logo_url: string }> {
  const formData = new FormData()
  formData.append('file', file)

  const response = await api
    .post(`api/hm/workspace/logo?variant=${variant}`, { body: formData })
    .json<{ logo_url: string }>()
  if (!response) {
    throw new Error('Failed to upload logo')
  }
  return response
}

export async function uploadWorkspaceBanner(file: File): Promise<{ banner_url: string }> {
  const formData = new FormData()
  formData.append('file', file, 'banner')

  const response = await api
    .post('api/hm/workspace/banner', { body: formData })
    .json<{ banner_url: string }>()
  if (!response) {
    throw new Error('Failed to upload banner')
  }
  return response
}

export async function fetchMembers(): Promise<WorkspaceMember[]> {
  const response = await get<WorkspaceMember[]>('api/hm/workspace/members')
  return response ?? []
}

export async function removeMember(memberId: number): Promise<void> {
  await del(`api/hm/workspace/members/${memberId}`)
}

export async function updateMemberRole(
  memberId: number,
  role: UpdateMemberRoleRequest
): Promise<void> {
  await put(`api/hm/workspace/members/${memberId}/role`, role)
}

export async function leaveWorkspace(): Promise<void> {
  await post('api/hm/workspace/leave', {})
}

export async function inviteMember(data: InviteToWorkspaceRequest): Promise<void> {
  await post('api/hm/workspace/invitations', data)
}

export async function fetchInvitations(): Promise<WorkspaceInvitation[]> {
  const response = await get<WorkspaceInvitation[]>('api/hm/workspace/invitations')
  return response ?? []
}

export async function cancelInvitation(invitationId: number): Promise<void> {
  await del(`api/hm/workspace/invitations/${invitationId}`)
}

export async function validateInvitation(token: string): Promise<WorkspaceInvitation> {
  const response = await get<WorkspaceInvitation>(`api/workspace/invitation/${token}`)
  if (!response) {
    throw new Error('Invitation not found')
  }
  return response
}

export async function acceptInvitation(token: string): Promise<WorkspaceWithMembers> {
  const response = await post<WorkspaceWithMembers>(`api/workspace/invitation/${token}/accept`, {})
  if (!response) {
    throw new Error('Failed to accept invitation')
  }
  return response
}

export async function declineInvitation(token: string): Promise<void> {
  await post(`api/workspace/invitation/${token}/decline`, {})
}

export async function getWorkspaceUsage(
  workspaceSlug: string | null = null
): Promise<WorkspaceUsageSummary> {
  const response = await get<WorkspaceUsageSummary>(
    'api/hm/workspace/usage',
    workspaceSlug ? workspaceHeaders(workspaceSlug) : undefined
  )
  if (!response) {
    throw new Error('Failed to get workspace usage')
  }
  return response
}

export async function getTrialProfile(): Promise<TrialProfile> {
  return get<TrialProfile>('api/hm/workspace/trial-profile')
}

export async function checkWorkspaceFeature(
  feature: string
): Promise<WorkspaceFeatureCheckResponse> {
  const response = await get<WorkspaceFeatureCheckResponse>(
    `api/hm/workspace/feature-check?feature=${feature}`
  )
  if (!response) {
    throw new Error('Failed to check workspace feature')
  }
  return response
}

export async function createWorkspaceCheckout(
  planKey: string,
  interval: 'month' | 'year'
): Promise<{ checkout_url: string }> {
  const response = await post<{ checkout_url: string }>('api/hm/workspace/checkout', {
    plan_key: planKey,
    interval,
  })
  if (!response) {
    throw new Error('Failed to start checkout')
  }
  return response
}

export async function createPilotCheckout(): Promise<{ checkout_url: string }> {
  const response = await post<{ checkout_url: string }>('api/hm/workspace/pilot/checkout', {})
  if (!response) {
    throw new Error('Failed to start the Pilot checkout')
  }
  return response
}

export async function getPilotCreditApplied(sessionId?: string): Promise<PilotCreditApplied> {
  const query = sessionId ? `?session_id=${encodeURIComponent(sessionId)}` : ''
  const response = await get<PilotCreditApplied>(`api/hm/workspace/pilot/credit/applied${query}`, {
    retry: 0,
  })
  return response ?? { applied: false }
}

export async function createWorkspacePortal(): Promise<{ portal_url: string }> {
  const response = await post<{ portal_url: string }>('api/hm/workspace/portal', {})
  if (!response) {
    throw new Error('Failed to open billing portal')
  }
  return response
}

export interface PlanChangePreview {
  direction: 'upgrade' | 'downgrade'
  scheduled: boolean
  proration_cents: number
  new_recurring_cents: number
  interval: 'month' | 'year'
  effective_at: number
  currency: string
}

export interface PlanChangeResult {
  direction: 'upgrade' | 'downgrade'
  effective_at: number
}

export interface PendingPlanChange {
  plan_key: string
  plan_name: string
  effective_at: number
}

export function isPaymentRequiredError(err: unknown): boolean {
  return (
    typeof err === 'object' &&
    err !== null &&
    'response' in err &&
    (err as { response?: { status?: number } }).response?.status === 402
  )
}

export async function previewWorkspacePlanChange(
  planKey: string,
  interval: 'month' | 'year'
): Promise<PlanChangePreview> {
  const response = await post<PlanChangePreview>('api/hm/workspace/plan-change/preview', {
    plan_key: planKey,
    interval,
  })
  if (!response) {
    throw new Error('Failed to preview plan change')
  }
  return response
}

export async function changeWorkspacePlan(
  planKey: string,
  interval: 'month' | 'year'
): Promise<PlanChangeResult> {
  const response = await post<PlanChangeResult>('api/hm/workspace/plan-change', {
    plan_key: planKey,
    interval,
  })
  if (!response) {
    throw new Error('Failed to change plan')
  }
  return response
}

export async function getWorkspacePendingPlanChange(): Promise<PendingPlanChange | null> {
  const response = await get<{ pending: PendingPlanChange | null }>(
    'api/hm/workspace/plan-change/pending'
  )
  return response?.pending ?? null
}

export async function cancelWorkspacePlanChange(): Promise<void> {
  await post('api/hm/workspace/plan-change/cancel', {})
}

export type AddonKey = 'seat' | 'position' | 'interview'

export interface AddonChangePreview {
  quantity: number
  proration_cents: number
  currency: string
}

export async function previewWorkspaceAddon(
  addon: AddonKey,
  quantity: number
): Promise<AddonChangePreview> {
  const response = await post<AddonChangePreview>('api/hm/workspace/addon/preview', {
    addon,
    quantity,
  })
  if (!response) {
    throw new Error('Failed to preview add-on change')
  }
  return response
}

export async function changeWorkspaceAddon(addon: AddonKey, quantity: number): Promise<void> {
  await post('api/hm/workspace/addon', { addon, quantity })
}

export async function buyWorkspaceInterviewPack(
  quantity: number
): Promise<{ checkout_url: string }> {
  const response = await post<{ checkout_url: string }>('api/hm/workspace/interviews/buy', {
    quantity,
  })
  if (!response) {
    throw new Error('Failed to start interview pack checkout')
  }
  return response
}

export async function requestPilot(): Promise<{ status: 'requested' }> {
  const response = await post<{ status: 'requested' }>('api/hm/workspace/pilot/request', {})
  if (!response) {
    throw new Error('Failed to request the Pilot')
  }
  return response
}

export interface MigrateContentResponse {
  migrated_members: number
}

export async function migrateWorkspaceContent(): Promise<MigrateContentResponse> {
  const response = await post<MigrateContentResponse>('api/hm/workspace/migrate-content', {})
  if (!response) {
    throw new Error('Failed to migrate content')
  }
  return response
}
